import {
  AuthState,
  authSlice,
  clearSignedOutByUser,
  setPhoneBindingEnforced,
  setPhoneBindingFlowOwner,
} from '@/services/authSlice';
import { BalanceHistoryState, balanceHistorySlice } from '@/services/balanceHistorySlice';
import { CookieConsentState, cookieConsentSlice } from '@/services/cookieConsentSlice';
import { CreateFormState, createFormSlice } from '@/services/createFormSlice';
import { DepartmentRedirectFormState, departmentRedirectFormSlice } from '@/services/departmentRedirectFormSlice';
import { FLUSH, PAUSE, PERSIST, PURGE, REGISTER, REHYDRATE, persistReducer, persistStore } from 'redux-persist';
import { HelpState, helpSlice } from '@/services/helpSlice';
import { IpLocationState, ipLocationSlice } from '@/services/ipLocationSlice';
import { LetterDraftState, letterDraftSlice } from '@/services/letter/LetterDraftSlice';
import { LetterInboxState, letterInboxSlice } from '@/services/letter/letterInboxSlice';
import { LetterOutboxState, letterOutboxSlice } from '@/services/letter/letterOutboxSlice';
import { PersistConfig, Persistor } from 'redux-persist/es/types';
import { ProfileState, profileSlice } from '@/services/profileSlice';
import { Reducer, combineReducers, configureStore } from '@reduxjs/toolkit';
import { SbpState, sbpSlice } from '@/services/sbpSlice';
import { apiSlice } from '@/services/api/apiSlice';
import { ipLocationMiddleware } from '@/app/middleware/ipLocationMiddleware';
import { stagedPhotoDropMiddleware } from '@/app/middleware/stagedPhotoDropMiddleware';
import { unauthorizedMiddleware } from '@/app/middleware/unauthorizedMiddleware';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import logger from 'redux-logger';
import storage from 'redux-persist/lib/storage';

interface ReducerState {
  auth: AuthState;
  letterInbox: LetterInboxState;
  letterOutbox: LetterOutboxState;
  createForm: CreateFormState;
  help: HelpState;
  profile: ProfileState;
  balanceHistory: BalanceHistoryState;
  departmentRedirectForm: DepartmentRedirectFormState;
  sbp: SbpState;
  ipLocation: IpLocationState;
  letterDraft: LetterDraftState;
  cookieConsent: CookieConsentState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  api: any;
}

export const rootReducer: Reducer<ReducerState> = combineReducers({
  auth: authSlice.reducer,
  letterInbox: letterInboxSlice.reducer,
  letterOutbox: letterOutboxSlice.reducer,
  createForm: createFormSlice.reducer,
  help: helpSlice.reducer,
  profile: profileSlice.reducer,
  balanceHistory: balanceHistorySlice.reducer,
  departmentRedirectForm: departmentRedirectFormSlice.reducer,
  sbp: sbpSlice.reducer,
  ipLocation: ipLocationSlice.reducer,
  letterDraft: letterDraftSlice.reducer,
  cookieConsent: cookieConsentSlice.reducer,
  [apiSlice.reducerPath]: apiSlice.reducer,
});

const config: PersistConfig<ReducerState> = {
  key: 'root',
  storage: storage,
  stateReconciler: autoMergeLevel2,
  whitelist: [
    'auth',
    'sbp',
    'letterInbox',
    'letterOutbox',
    'letterDraft',
    'createForm',
    'balanceHistory',
    'cookieConsent',
  ],
};

const reducer = persistReducer(config, rootReducer);

const devMode: boolean = process.env.NODE_ENV !== 'production';

export const store = configureStore({
  reducer: reducer,
  middleware: (getDefaultMiddleware) => {
    const defaultMiddleware = getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(apiSlice.middleware)
      .concat(ipLocationMiddleware)
      .concat(unauthorizedMiddleware)
      .concat(stagedPhotoDropMiddleware);
    return devMode ? defaultMiddleware.concat(logger) : defaultMiddleware;
  },
});

export const persistor: Persistor = persistStore(store, null, (): void => {
  // `auth` is persisted, but a phone-binding refusal records what the server answered during one browsing session
  // rather than anything the user chose.
  // Restoring it would keep the barrier up for someone who has since bound a number on another device,
  // and it would survive the feature being switched off, so each load starts from no refusal —
  // a user who really is barred gets one again from their first cabinet request.
  // Cleared here rather than on `REHYDRATE` because `autoMergeLevel2` skips merging any key the reducer touched,
  // and clearing it there would discard the persisted session instead.
  store.dispatch(setPhoneBindingEnforced(false));
  // A binding under way belongs to the dialog that was on screen, and a reload has taken that dialog away.
  // Restoring the owner would leave both mounts deferring to a flow nobody is running, and so show no dialog at all.
  store.dispatch(setPhoneBindingFlowOwner(undefined));
  // Signing out is a moment, not a state to restore.
  // It is remembered only so that the sign-out already under way lands on the main page instead of a login form;
  // a later visit to a cabinet address deserves the form and the return trip it offers,
  // even when the last thing that happened was a sign-out.
  store.dispatch(clearSignedOutByUser());
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
